import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FcdlModalService, ModalType } from '@fmr-ap001330/fc-modern-angular-modules';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { DigitalPayeeEnrollment } from '../../models/digital-payee-enrollment.model';
import { DigitalPayeeStatus } from '../../models/digital-payee-status.enum';
import { NpoSearchModalContainerComponent } from '../../npo-search-modal/npo-search-modal.container.component';
import { DppEnrollmentService } from '../../services/dpp-enrollment.service';
import { NpoSearchService } from '../../services/npo-search.service';
import {
    activate,
    activateError,
    activateSuccess,
    continuedToStripe,
    enroll,
    enrollError,
    enrollmentStatusError,
    enrollmentStatusNotFound,
    enrollmentStatusSuccess,
    enrollmentSubmitted,
    enrollSuccess,
    getEnrollmentStatus,
    npoSearched,
    npoSearchError,
    npoSearchModalOpened,
    npoSearchSuccess,
    npoSelected,
    pageChanged,
    postNpoSearch,
    returnedFromStripe,
} from '../actions/dpp-enrollment.actions';
import { DppEnrollmentFacade } from '../facade/dpp-enrollment.facade';

@Injectable({
    providedIn: 'root',
})
export class DppEnrollmentEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly router: Router,
        private readonly npoSearchService: NpoSearchService,
        private readonly dppEnrollmentService: DppEnrollmentService,
        private readonly dppEnrollmentFacade: DppEnrollmentFacade,
        private readonly modalService: FcdlModalService,
    ) {}

    returnedFromStripe$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(returnedFromStripe),
                tap(() => {
                    this.router.navigateByUrl('/dpp');
                }),
            ),
        {
            dispatch: false,
        },
    );

    npoSearchModalOpened$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(npoSearchModalOpened),
                tap(() => {
                    this.modalService.open({
                        component: NpoSearchModalContainerComponent,
                        type: ModalType.search,
                        title: 'Find your nonprofit organization',
                        size: 'fit',
                    });
                }),
            ),
        { dispatch: false },
    );

    npoSelected$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(npoSelected),
                tap(() => {
                    this.modalService.close();
                }),
            ),
        { dispatch: false },
    );

    npoSearched$ = createEffect(() =>
        this.actions$.pipe(
            ofType(npoSearched, pageChanged),
            map(() => postNpoSearch()),
        ),
    );

    postNpoSearch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(postNpoSearch),
            withLatestFrom(this.dppEnrollmentFacade.npoSearchRequest$),
            concatMap(([, npoSearchRequest]) => {
                return this.npoSearchService
                    .npoSearch(npoSearchRequest!.ein, npoSearchRequest!.pageNumber)
                    .pipe(
                        map((npoSearchResults) => npoSearchSuccess({ npoSearchResults })),
                        catchError(() => {
                            return of(npoSearchError());
                        }),
                    );
            }),
        ),
    );

    continuedToStripe$ = createEffect(() =>
        this.actions$.pipe(
            ofType(continuedToStripe),
            withLatestFrom(
                this.dppEnrollmentFacade.enrollmentStatus$,
                this.dppEnrollmentFacade.selectedNpo$,
                this.dppEnrollmentFacade.partnershipChanged$,
            ),
            map(([, enrollmentStatus, selectedNpo, partnershipChanged]) => {
                // If digital payee id exists, use that to resume enrollment process. Otherwise, submit details for the first time.
                const enrollment: DigitalPayeeEnrollment = enrollmentStatus?.digitalPayeeId
                    ? {
                          digitalPayeeId: enrollmentStatus.digitalPayeeId,
                      }
                    : {
                          sourceSystem: 'FC',
                          npoId: selectedNpo?.secondaryCharityId || selectedNpo?.charityId,
                          hasPartnership: selectedNpo?.isPartner || partnershipChanged,
                          taxId: selectedNpo?.taxId,
                          isPrimaryNpo: Boolean(!selectedNpo?.secondaryCharityId),
                      };
                return enroll({ enrollment });
            }),
        ),
    );

    enroll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(enroll),
            concatMap((action) => {
                return this.dppEnrollmentService.enroll(action.enrollment).pipe(
                    map((enrollment) => enrollSuccess({ enrollment })),
                    catchError(() => {
                        return of(enrollError());
                    }),
                );
            }),
        ),
    );

    enrollSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(enrollSuccess),
                tap((action) => {
                    // Navigate to stripe
                    window.open(action.enrollment.registrationUrl!, '_self');
                }),
            ),
        { dispatch: false },
    );

    apiErrors$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(enrollError, activateError, enrollmentStatusError),
                tap(() => {
                    // Route to error page
                    return this.router.navigateByUrl('/dpp/error');
                }),
            ),
        { dispatch: false },
    );

    enrollmentSubmitted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(enrollmentSubmitted),
            withLatestFrom(
                this.dppEnrollmentFacade.enrollmentStatus$,
                this.dppEnrollmentFacade.selectedNpo$,
                this.dppEnrollmentFacade.partnershipChanged$,
            ),
            map(([, enrollmentStatus, selectedNpo, partnershipChanged]) =>
                activate({
                    digitalPayeeId: enrollmentStatus!.digitalPayeeId,
                    hasPartnership: selectedNpo!.isPartner || partnershipChanged,
                }),
            ),
        ),
    );

    activate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(activate),
            concatMap((action) => {
                return this.dppEnrollmentService
                    .activate(action.digitalPayeeId, action.hasPartnership)
                    .pipe(
                        map((enrollmentStatus) => activateSuccess({ enrollmentStatus })),
                        catchError(() => {
                            return of(activateError());
                        }),
                    );
            }),
        ),
    );

    activateSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(activateSuccess),
                tap(() => {
                    // Route to success
                    this.router.navigateByUrl('/dpp/success');
                }),
            ),
        { dispatch: false },
    );

    npoSelectedOrReturnedFromStripe$ = createEffect(() =>
        this.actions$.pipe(
            ofType(npoSelected, returnedFromStripe),
            map(() => getEnrollmentStatus()),
        ),
    );

    getEnrollmentStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getEnrollmentStatus),
            withLatestFrom(
                this.dppEnrollmentFacade.enrollmentStatus$,
                this.dppEnrollmentFacade.selectedNpo$,
            ),
            concatMap(([, enrollmentStatus, selectedNpo]) => {
                const options = enrollmentStatus?.digitalPayeeId
                    ? { digitalPayeeId: enrollmentStatus.digitalPayeeId }
                    : {
                          npoId: selectedNpo?.secondaryCharityId || selectedNpo?.charityId,
                          isSecondary: Boolean(selectedNpo?.secondaryCharityId),
                      };
                return this.dppEnrollmentService.getEnrollmentStatus(options).pipe(
                    map((enrollmentStatus) => enrollmentStatusSuccess({ enrollmentStatus })),
                    catchError((err: HttpErrorResponse) => {
                        // Check status code for 404
                        if (err.status === 404) {
                            return of(enrollmentStatusNotFound());
                        }
                        return of(enrollmentStatusError());
                    }),
                );
            }),
        ),
    );

    enrollmentStatusSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(enrollmentStatusSuccess),
                withLatestFrom(this.dppEnrollmentFacade.returnedFromStripe$),
                filter(([, returnedFromStripe]) => returnedFromStripe),
                tap(([enrollmentStatus]) => {
                    if (
                        enrollmentStatus.enrollmentStatus.status === DigitalPayeeStatus.ACTIVE ||
                        enrollmentStatus.enrollmentStatus.status ===
                            DigitalPayeeStatus.AWAITING_VERIFICATION
                    ) {
                        // Route to success
                        this.router.navigateByUrl('/dpp/success');
                    } else if (
                        enrollmentStatus.enrollmentStatus.status === DigitalPayeeStatus.INITIATED
                    ) {
                        // Route to incomplete
                        this.router.navigateByUrl('/dpp/incomplete');
                    }
                }),
            ),
        { dispatch: false },
    );
}
